import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { LogIn, AlertCircle } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { ApiError } from '../lib/api'
import { DJ_NAME } from '../data'

export default function SignIn() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string })?.from ?? '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await signIn(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign in failed')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'var(--ink)', border: '1px solid var(--border)',
    color: 'var(--cream)', fontFamily: 'var(--font-body)', fontSize: '14px',
    padding: '12px 14px', outline: 'none', transition: 'border-color 0.2s', borderRadius: 0,
  }
  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '3px',
    textTransform: 'uppercase', color: 'var(--gold-dim)', display: 'block', marginBottom: '8px',
  }

  return (
    <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', background: 'var(--ink)' }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>
        <Link to="/" style={{ display: 'block', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '20px', letterSpacing: '5px', color: 'var(--gold)', textTransform: 'uppercase', textDecoration: 'none', textAlign: 'center', marginBottom: '40px' }}>
          {DJ_NAME}
        </Link>
        <div style={{ background: 'var(--ink-2)', border: '1px solid var(--border)', padding: '40px 32px' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '24px', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--cream)', marginBottom: '28px', textAlign: 'center' }}>
            Sign In
          </h1>
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#e08080', padding: '10px 14px', marginBottom: '20px', fontSize: '13px' }}>
              <AlertCircle size={14} />{error}
            </div>
          )}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--border)')} />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--border)')} />
            </div>
            <button type="submit" disabled={loading} className="btn-primary"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', opacity: loading ? 0.7 : 1, cursor: loading ? 'wait' : 'pointer' }}>
              <LogIn size={14} />{loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: 'var(--muted)' }}>
            Don't have an account?{' '}
            <Link to="/signup" style={{ color: 'var(--gold)', textDecoration: 'none' }}>Sign Up</Link>
          </p>
        </div>
      </div>
    </main>
  )
}
